import React from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
import { useSnackbar } from 'notistack';

import { ProfileActionsType } from '../../../../types/menu';
import { utils } from '../../../../utils';
import { Track } from '../../../../weborder/analytics';
import * as Api from '../../../../weborder/Api';
import { cookieConsent } from '../../../../weborder/cookieconsent';
import { InlineEditableField, List, Select, Switch } from '../../../atoms';
import { ListItem as ProfileListItem } from '../../../atoms/List';
import {
  ArrowFilledDown as ArrowFilledDownIcon,
  Card as CardIcon,
  ChevronRight as ChevronRightIcon,
  Email as EmailIcon,
  History as HistoryIcon,
  Label as LabelIcon,
  Language as LanguageIcon,
  Lock as LockIcon,
  Login as LoginIcon,
  Message as MessageIcon,
  Profile as ProfileIcon,
  Secured as SecuredIcon,
} from '../../../icons';

type Props = {
  actions: ProfileActionsType;
  className?: string;
  configData: Api.AppConfig;
  contactFormType: Api.ContactFormType;
  customerData: Api.Customer;
  hasDisableNonEssentialCookies: boolean;
  hasGdprCookieFeatureFlag: boolean;
  languageCode: string;
  onOpenPayments: () => void;
  translate: any;
};

const useStyles = makeStyles((theme: Theme) => ({
  languageSelect: {
    '& .fd-icon': {
      position: 'absolute',
      right: 0,
      pointerEvents: 'none',
    },
  },
  profileList: {
    '& .MuiListItem-root': {
      paddingBottom: theme.spacing(1.5),
      paddingTop: theme.spacing(1.5),
      paddingRight: theme.spacing(2),
    },
  },
}));

export function ProfileList(props: Props): JSX.Element {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const {
    actions,
    className,
    configData,
    contactFormType,
    customerData,
    hasDisableNonEssentialCookies,
    hasGdprCookieFeatureFlag,
    languageCode,
    onOpenPayments,
    translate,
  } = props;

  const profileListClasses = clsx(classes.profileList, className);

  const getAvailableLanguageOptions = () => {
    const langOptions = [];
    configData.Languages.forEach((language: Api.Language) =>
      langOptions.push({
        disabled: false,
        key: language.LangageId,
        label: language.Name,
      }),
    );
    return langOptions;
  };

  const handleEmailValidation = (value: string): boolean =>
    utils.check.isValidEmail(value);

  const handlePromotions = () => {
    Track('Profile', 'Receive Promotions', {
      event_label: (!customerData.SmsPromotionsEnabled).toString(),
      label: 'Receive Promotions',
      section: 'Profile',
    });
    actions.onChangePromotions(!customerData.SmsPromotionsEnabled);
  };

  const handlePrivacySettings = () => cookieConsent.edit();

  const handleNameChange = (newName: string): void => {
    if (newName !== customerData.CustomerName) {
      actions.onChangeName(newName, enqueueSnackbar);
    }
  };

  const handleEmailChange = (newEmail: string): void => {
    if (newEmail !== customerData.EmailAddress) {
      actions.onChangeEmail(newEmail, enqueueSnackbar);
    }
  };

  const hasLanguages = configData.Languages.length > 1;
  const hasContactUs = contactFormType === Api.ContactFormType.Intercom;
  const hasPrivacySettings =
    hasGdprCookieFeatureFlag &&
    !hasDisableNonEssentialCookies &&
    !utils.check.isElixirHosted();

  const profileItems = {
    customerName: {
      action: () => {},
      dataFd: 'profile-name',
      key: 'profile-name',
      label: (
        <InlineEditableField
          action={handleNameChange}
          value={customerData.CustomerName}
        />
      ),
      leftIcon: <ProfileIcon />,
      rightIcon: null,
    },
    customerEmail: {
      action: () => {},
      dataFd: 'profile-email',
      key: 'profile-email',
      label: (
        <InlineEditableField
          action={handleEmailChange}
          validation={handleEmailValidation}
          value={customerData.EmailAddress}
        />
      ),
      leftIcon: <EmailIcon />,
      rightIcon: null,
    },
    previousOrders: {
      action: actions.onNavigateToPreviousOrders,
      dataFd: 'profile-previous-orders',
      key: 'profile-previous-orders',
      label: translate('HistoryPage_PreviousOrders_heading'),
      leftIcon: <HistoryIcon />,
      rightIcon: <ChevronRightIcon />,
    },
    paymentMethods: {
      action: () => onOpenPayments(),
      dataFd: 'profile-payment-methods',
      key: 'profile-payment-method',
      label: translate('Payment_methods'),
      leftIcon: <CardIcon />,
      rightIcon: <ChevronRightIcon />,
    },
    receivePromotions: {
      action: handlePromotions,
      dataFd: 'profile-receive-promotions',
      key: 'profile-receive-promotions',
      label: translate('Receive_promotions'),
      leftIcon: <LabelIcon />,
      rightIcon: <Switch checked={customerData.SmsPromotionsEnabled} />,
    },
    languageSelector: {
      action: () => {},
      dataFd: 'profile-language-selector',
      key: 'profile-language-selector',
      label: (
        <Select
          aria-label={translate('Select_language')}
          id="language-selector"
          IconComponent={ArrowFilledDownIcon}
          isNative
          menuItems={getAvailableLanguageOptions()}
          onChange={(event) =>
            actions.onChangeLanguage(event.target.value as string)
          }
          value={languageCode}
        />
      ),
      leftIcon: <LanguageIcon />,
      rightIcon: null,
    },
    contactUs: {
      action: actions.onContactUs,
      dataFd: 'profile-contact-us',
      key: 'profile-contact-us',
      label: translate('Contact_us'),
      leftIcon: <MessageIcon />,
      rightIcon: null,
    },
    privacySettings: {
      action: handlePrivacySettings,
      dataFd: 'profile-privacy-settings',
      key: 'profile-privacy-settings',
      label: translate('Privacy_Settings'),
      leftIcon: <LockIcon />,
      rightIcon: <ChevronRightIcon />,
    },
    aboutData: {
      action: actions.onAboutMyData,
      dataFd: 'profile-about-data',
      key: 'profile-about-data',
      label: translate('About_my_data'),
      leftIcon: <SecuredIcon />,
      rightIcon: <ChevronRightIcon />,
    },
    logout: {
      action: actions.onLogOut,
      dataFd: 'profile-logout',
      key: 'profile-logout',
      label: translate('Log_out'),
      leftIcon: <LoginIcon />,
      rightIcon: null,
    },
  };

  if (!hasLanguages) {
    delete profileItems.languageSelector;
  }
  if (!hasContactUs) {
    delete profileItems.contactUs;
  }
  if (!hasPrivacySettings) {
    delete profileItems.privacySettings;
  }

  const items: ProfileListItem[] = Object.values(profileItems).map((item) => ({
    key: item.key,
    leftIcon: item.leftIcon,
    onClick: item.action,
    primaryContent: item.label,
    rightIcon: item.rightIcon,
    testId: item.dataFd,
  }));

  return <List className={profileListClasses} items={items} />;
}
